import {
  createAsyncThunk,
  createSelector,
  createSlice,
  SerializedError,
} from "@reduxjs/toolkit";

import { SearchService } from "services";
import {
  IAPIState,
  IStoreState,
  IErrorPayload,
  ISearchResults,
  ISearchResult,
  ISearchData,
} from "types";

const NB_SEARCH_RESULT_ITEMS = 50;

export interface ISearchFilterState {
  [id: string]: {
    data?: ISearchResult[];
    meta: {
      status: IAPIState;
      error?: SerializedError | null;
      hasSearched?: boolean;
    };
  };
}

const initData: ISearchResult[] = [];

const initialMeta = {
  status: IAPIState.idle,
  error: null,
  hasSearched: false,
};
const initialState: ISearchFilterState = {};

export const doSearchFilter = createAsyncThunk<
  // Return type of the payload creator
  ISearchResults,
  // First argument to the payload creator
  {
    // id = content block id
    id: string;
    data: Partial<ISearchData>;
    parentToolId?: string;
  },
  // Types for ThunkAPI
  { rejectValue: IErrorPayload }
>("searchFilter/search", async (searchFilterData, { rejectWithValue }) => {
  try {
    const { data, parentToolId } = searchFilterData;

    return await SearchService.search({
      page: 1, // only fetch the first page
      query: data.query || "",
      amount: NB_SEARCH_RESULT_ITEMS,
      type: data.type || undefined,
      ...(parentToolId && { facets: { parentTool: [parentToolId] } }),
      prioritiseDossiersAndTools: !data.type,
    });
  } catch (error) {
    return rejectWithValue(error as IErrorPayload);
  }
});

const searchFilterSlice = createSlice({
  name: "searchFilter",
  initialState,
  reducers: {
    clearSearchFilterState: (state, { payload }) => {
      state[payload].data = [];
      state[payload].meta = initialMeta;
    },
  },
  extraReducers: (builder) => {
    // search pending
    builder.addCase(doSearchFilter.pending, (state, { meta }) => {
      state[meta.arg.id] = {
        ...state[meta.arg.id],
        meta: {
          status: IAPIState.pending,
          error: null,
          hasSearched: true,
        },
      };
    });
    // search failed
    builder.addCase(doSearchFilter.rejected, (state, { meta, payload }) => {
      state[meta.arg.id] = {
        ...state[meta.arg.id],
        meta: {
          status: IAPIState.failed,
          error: payload?.error,
          hasSearched: true,
        },
      };
    });
    // search succeeded
    builder.addCase(doSearchFilter.fulfilled, (state, { payload, meta }) => {
      state[meta.arg.id] = {
        ...state[meta.arg.id],
        meta: {
          status: IAPIState.succeeded,
          error: null,
          hasSearched: true,
        },
        data: payload.results,
      };
    });
  },
});

// content block id selector
export const searchFilterIdSelector = (id: string) =>
  createSelector(
    (state: IStoreState) => state.searchFilter,
    (searchFilter) => searchFilter[id]
  );

export const searchFilterMetaSelector = (id: string) =>
  createSelector(
    searchFilterIdSelector(id),
    (searchFilter) => searchFilter?.meta || initialMeta
  );

export const searchFilterDataSelector = (id: string) =>
  createSelector(
    searchFilterIdSelector(id),
    (searchFilter) => searchFilter?.data || initData
  );

export const { clearSearchFilterState } = searchFilterSlice.actions;
export const searchFilterReducer = searchFilterSlice.reducer;
